import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Handshake, Plus, Trash2, Wallet, TrendingUp, ArrowUpCircle, ArrowDownCircle, BookOpen, Pencil, Search } from "lucide-react";
import { inr, inrFull, fmtDate } from "@/utils";
import { toast } from "sonner";
import { usePagination } from "@/components/Pagination";

const TXN_TYPES = [
  { v: "investment", l: "Investment (Capital In)", tint: "text-emerald-700", Icon: ArrowUpCircle },
  { v: "withdrawal", l: "Withdrawal (Capital Out)", tint: "text-red-600", Icon: ArrowDownCircle },
  { v: "profit_credit", l: "Profit Credit", tint: "text-emerald-700", Icon: TrendingUp },
  { v: "expense_debit", l: "Expense Debit", tint: "text-red-600", Icon: ArrowDownCircle },
  { v: "adjustment", l: "Adjustment", tint: "text-zinc-600", Icon: Pencil },
];

const PAY_MODES = ["bank_transfer", "cheque", "upi", "rtgs", "cash"];

const EMPTY_PARTNER = {
  project_id: "", name: "", role: "", phone: "", email: "", pan: "", aadhaar: "",
  gst: "", address: "", share_percent: 0, committed_capital: 0, notes: "", active: true,
};

export default function Partners() {
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const [projectId, setProjectId] = useState("");

  const { data: summary } = useQuery({
    queryKey: ["partners-summary", projectId],
    queryFn: async () => (await api.get("/partners/summary", { params: projectId ? { project_id: projectId } : {} })).data,
  });

  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(EMPTY_PARTNER);
  const [ledgerId, setLedgerId] = useState(null);
  const [search, setSearch] = useState("");

  const partners = useMemo(() => {
    const list = summary?.partners || [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.role || "").toLowerCase().includes(q) ||
      (p.phone || "").toLowerCase().includes(q) ||
      (p.pan || "").toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q)
    );
  }, [summary, search]);
  const totals = summary?.totals || {};

  const { pageRows: pagedPartners, controls: partnerControls } = usePagination(partners);

  async function saveNew() {
    if (!form.project_id) return toast.error("Select a project");
    if (!form.name?.trim()) return toast.error("Partner name required");
    try {
      await api.post("/partners", {
        ...form,
        share_percent: +form.share_percent || 0,
        committed_capital: +form.committed_capital || 0,
      });
      toast.success("Partner added");
      qc.invalidateQueries({ queryKey: ["partners-summary"] });
      setOpenNew(false);
      setForm(EMPTY_PARTNER);
    } catch (e) { toast.error(apiErr(e, "Failed to add partner")); }
  }

  async function removePartner(p) {
    if (!window.confirm(`Remove partner "${p.name}"? Ledger transactions will block deletion.`)) return;
    try {
      await api.delete(`/partners/${p.id}`);
      toast.success("Partner removed");
      qc.invalidateQueries({ queryKey: ["partners-summary"] });
    } catch (e) {
      const detail = apiErr(e);
      if (detail.toLowerCase().includes("force")) {
        if (window.confirm(`${detail}\n\nDelete partner and ALL their transactions?`)) {
          await api.delete(`/partners/${p.id}?force=true`);
          toast.success("Partner and their ledger removed");
          qc.invalidateQueries({ queryKey: ["partners-summary"] });
        }
      } else { toast.error(detail); }
    }
  }

  return (
    <div className="space-y-6" data-testid="partners-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Partnership Projects</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Project Partners</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Manage capital partners, their share of the project, and see each partner&apos;s live ledger with investments,
            withdrawals and profit share auto-computed from the project P&amp;L.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              placeholder="Search partner name, role, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-64 pl-8"
              data-testid="partner-search"
            />
          </div>
          <Select value={projectId || "__all__"} onValueChange={(v) => setProjectId(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-64" data-testid="partners-project-filter">
              <SelectValue placeholder="Filter by project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-partner-btn">
                <Plus size={14} className="mr-1" /> Add Partner
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-heading">Add Partner</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Project *</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger data-testid="new-partner-project"><SelectValue placeholder="Choose project" /></SelectTrigger>
                    <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-partner-name" /></div>
                <div><Label>Role</Label><Input placeholder="e.g., Managing Partner" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} /></div>
                <div><Label>Share (%) *</Label><Input type="number" step="0.01" value={form.share_percent} onChange={(e) => setForm({ ...form, share_percent: e.target.value })} data-testid="new-partner-share" /></div>
                <div><Label>Committed Capital (₹)</Label><Input type="number" value={form.committed_capital} onChange={(e) => setForm({ ...form, committed_capital: e.target.value })} data-testid="new-partner-committed" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} /></div>
                <div><Label>Aadhaar</Label><Input value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} /></div>
                <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button onClick={saveNew} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-partner-btn">Save Partner</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-white border-zinc-200"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Partners</div>
          <div className="text-xl font-bold font-mono mt-1">{totals.count || 0}</div>
        </CardContent></Card>
        <Card className="bg-white border-zinc-200"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Share Allocated</div>
          <div className="text-xl font-bold font-mono mt-1">{totals.share_allocated || 0}%</div>
        </CardContent></Card>
        <Card className="bg-white border-zinc-200"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Unallocated</div>
          <div className={`text-xl font-bold font-mono mt-1 ${totals.share_unallocated > 0 ? "text-amber-700" : "text-emerald-700"}`}>
            {totals.share_unallocated || 0}%
          </div>
        </CardContent></Card>
        <Card className="bg-white border-zinc-200"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Invested</div>
          <div className="text-xl font-bold font-mono mt-1 text-emerald-700">{inr(totals.invested || 0)}</div>
        </CardContent></Card>
        <Card className="bg-white border-zinc-200"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Projected Profit</div>
          <div className="text-xl font-bold font-mono mt-1">{inr(totals.profit_projected || 0)}</div>
        </CardContent></Card>
      </div>

      {/* Partner list */}
      <Card className="bg-white border-zinc-200">
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="font-heading text-base flex items-center gap-2"><Handshake size={16} /> Partners</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-zinc-50"><TableRow>
              <TableHead>Partner</TableHead>
              <TableHead>Project</TableHead>
              <TableHead>Share</TableHead>
              <TableHead className="text-right">Committed</TableHead>
              <TableHead className="text-right">Invested</TableHead>
              <TableHead className="text-right">Withdrawn</TableHead>
              <TableHead className="text-right">Profit Share (Proj.)</TableHead>
              <TableHead className="text-right">Current Balance</TableHead>
              <TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {pagedPartners.map((p) => {
                const proj = projects.find((x) => x.id === p.project_id);
                return (
                  <TableRow key={p.id} className="data-row" data-testid={`partner-row-${p.id}`}>
                    <TableCell>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-[10px] text-zinc-500">{p.role || "Partner"}{p.phone ? ` · ${p.phone}` : ""}</div>
                    </TableCell>
                    <TableCell className="text-xs text-zinc-600">{proj?.name || "—"}</TableCell>
                    <TableCell><span className="text-xs font-mono font-semibold text-emerald-700">{p.share_percent}%</span></TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(p.committed_capital)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-700">{inr(p.invested)}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{inr(p.withdrawn)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {inr(p.projected_profit_share)}
                      {p.profit_pending > 0 && (
                        <div className="text-[9px] uppercase tracking-widest text-amber-700">
                          {inr(p.profit_pending)} pending
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">{inr(p.current_balance)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setLedgerId(p.id)} className="mr-1" data-testid={`open-ledger-${p.id}`}>
                        <BookOpen size={12} className="mr-1" /> Ledger
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => removePartner(p)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`del-partner-${p.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!partners.length && (
                <TableRow><TableCell colSpan={9} className="text-center text-sm text-zinc-500 py-14">
                  No partners yet. Click <b>Add Partner</b> to start tracking capital and profit shares.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {partnerControls}
        </CardContent>
      </Card>

      {ledgerId && <PartnerLedger partnerId={ledgerId} onClose={() => setLedgerId(null)} />}
    </div>
  );
}


function PartnerLedger({ partnerId, onClose }) {
  const qc = useQueryClient();
  const { data: ledger, refetch } = useQuery({
    queryKey: ["partner-ledger", partnerId],
    queryFn: async () => (await api.get(`/partners/${partnerId}/ledger`)).data,
    enabled: !!partnerId,
  });

  const [txnOpen, setTxnOpen] = useState(false);
  const [txn, setTxn] = useState({
    type: "investment", amount: 0, date: new Date().toISOString().slice(0, 10),
    payment_mode: "bank_transfer", reference_no: "", description: "",
  });

  async function saveTxn() {
    if (!(+txn.amount > 0)) return toast.error("Enter amount");
    try {
      await api.post("/partner-txns", {
        ...txn,
        amount: +txn.amount,
        partner_id: partnerId,
        project_id: ledger.partner.project_id,
      });
      toast.success("Ledger entry recorded");
      qc.invalidateQueries({ queryKey: ["partner-ledger", partnerId] });
      qc.invalidateQueries({ queryKey: ["partners-summary"] });
      setTxnOpen(false);
      setTxn({ ...txn, amount: 0, reference_no: "", description: "" });
      refetch();
    } catch (e) { toast.error(apiErr(e, "Failed")); }
  }

  async function delTxn(id) {
    if (!window.confirm("Delete this ledger entry?")) return;
    try {
      await api.delete(`/partner-txns/${id}`);
      qc.invalidateQueries({ queryKey: ["partner-ledger", partnerId] });
      qc.invalidateQueries({ queryKey: ["partners-summary"] });
      toast.success("Entry removed");
      refetch();
    } catch (e) { toast.error(apiErr(e)); }
  }

  const p = ledger?.partner;

  const typeMap = useMemo(() => {
    const m = {};
    for (const t of TXN_TYPES) m[t.v] = t;
    return m;
  }, []);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="partner-ledger-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {p?.name || "Partner"} · Ledger
            <span className="ml-2 text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-300 px-1.5 py-0.5 rounded">
              {p?.share_percent}% share
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SmallStat label="Committed" value={inrFull(p?.committed_capital || 0)} />
          <SmallStat label="Invested" value={inrFull(p?.invested || 0)} tint="text-emerald-700" />
          <SmallStat label="Withdrawn" value={inrFull(p?.withdrawn || 0)} tint="text-red-600" />
          <SmallStat label="Profit Credited" value={inrFull(p?.profit_credited || 0)} />
          <SmallStat label="Current Balance" value={inrFull(p?.current_balance || 0)} tint="font-bold" />
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-900">
          <div className="flex items-center gap-2 flex-wrap">
            <TrendingUp size={14} />
            <span>
              Projected profit share from project P&amp;L: <b className="font-mono">{inrFull(ledger?.projected_profit_share || 0)}</b>
              {" · "}
              Credited: <b className="font-mono">{inrFull(ledger?.profit_credited || 0)}</b>
              {" · "}
              Pending: <b className={`font-mono ${(ledger?.profit_pending || 0) > 0 ? "text-amber-800" : "text-emerald-800"}`}>
                {inrFull(ledger?.profit_pending || 0)}
              </b>
            </span>
          </div>
          <div className="mt-1 text-[10px] text-emerald-800">
            Project Gross Profit: {inrFull(ledger?.project_pnl?.gross_profit || 0)} × {p?.share_percent || 0}% share
          </div>
        </div>

        <div className="flex justify-between items-center mt-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Transactions</div>
          <Dialog open={txnOpen} onOpenChange={setTxnOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-partner-txn-btn">
                <Plus size={12} className="mr-1" /> Add Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">New Ledger Entry</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Type</Label>
                  <Select value={txn.type} onValueChange={(v) => setTxn({ ...txn, type: v })}>
                    <SelectTrigger data-testid="new-txn-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TXN_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Amount (₹)</Label><Input type="number" value={txn.amount} onChange={(e) => setTxn({ ...txn, amount: e.target.value })} data-testid="new-txn-amount" /></div>
                <div><Label>Date</Label><Input type="date" value={txn.date} onChange={(e) => setTxn({ ...txn, date: e.target.value })} /></div>
                <div>
                  <Label>Payment Mode</Label>
                  <Select value={txn.payment_mode} onValueChange={(v) => setTxn({ ...txn, payment_mode: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PAY_MODES.map((m) => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Reference #</Label><Input value={txn.reference_no} onChange={(e) => setTxn({ ...txn, reference_no: e.target.value })} /></div>
                <div className="col-span-2"><Label>Description</Label><Input value={txn.description} onChange={(e) => setTxn({ ...txn, description: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button onClick={saveTxn} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-partner-txn">Record</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="border rounded overflow-hidden mt-2 max-h-[400px] overflow-y-auto">
          <Table>
            <TableHeader className="bg-zinc-50 sticky top-0"><TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Ref</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {(ledger?.transactions || []).map((t) => {
                const cfg = typeMap[t.type] || {};
                const Icon = cfg.Icon || Wallet;
                return (
                  <TableRow key={t.id} className="data-row" data-testid={`txn-row-${t.id}`}>
                    <TableCell className="font-mono text-xs">{fmtDate(t.date)}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-xs ${cfg.tint || "text-zinc-600"}`}>
                        <Icon size={12} /> {cfg.l?.split(" ")[0] || t.type}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-zinc-600 truncate max-w-[200px]">{t.description || "—"}</TableCell>
                    <TableCell className="text-xs font-mono text-zinc-500">{t.reference_no || "—"}</TableCell>
                    <TableCell className={`text-right font-mono ${cfg.tint || ""}`}>{inr(t.amount)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{inr(t.balance)}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => delTxn(t.id)} className="text-red-600" data-testid={`del-txn-${t.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!(ledger?.transactions || []).length && (
                <TableRow><TableCell colSpan={7} className="text-center text-xs text-zinc-500 py-8">
                  No transactions yet — record the first investment above.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SmallStat({ label, value, tint = "" }) {
  return (
    <Card className="bg-white border-zinc-200"><CardContent className="p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-sm font-bold font-mono mt-1 ${tint}`}>{value}</div>
    </CardContent></Card>
  );
}
